import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { parcelAPI } from '../../services/api';
import AppNavbar from '../../components/AppNavbar';
import SiteFooter from '../../components/SiteFooter';
import MobileBottomNav from '../../components/MobileBottomNav';
import { arPrice } from '../../utils/arFormat';

const STATUS_CFG = {
  draft: { label: 'مسودة', color: 'bg-gray-100 text-gray-600' },
  payment_pending: { label: 'بانتظار الدفع', color: 'bg-amber-100 text-amber-700' },
  payment_submitted: { label: 'قيد المراجعة', color: 'bg-blue-100 text-blue-700' },
  paid: { label: 'تم الدفع', color: 'bg-emerald-100 text-emerald-700' },
  waiting_delivery: { label: 'بانتظار التسليم للمكتب', color: 'bg-amber-100 text-amber-700' },
  received_by_office: { label: 'مستلم بالمكتب', color: 'bg-blue-100 text-blue-700' },
  waiting_transportation: { label: 'بانتظار رحلة', color: 'bg-gray-100 text-gray-600' },
  loaded: { label: 'محمّل على الباص', color: 'bg-indigo-100 text-indigo-700' },
  in_transit: { label: 'في الطريق', color: 'bg-purple-100 text-purple-700' },
  arrived: { label: 'وصل الوجهة', color: 'bg-teal-100 text-teal-700' },
  ready_for_collection: { label: 'جاهز للاستلام', color: 'bg-emerald-100 text-emerald-700' },
  completed: { label: 'مكتمل', color: 'bg-emerald-100 text-emerald-700' },
  returned: { label: 'مرتجع', color: 'bg-red-100 text-red-700' },
  cancelled: { label: 'ملغي', color: 'bg-red-100 text-red-700' },
  rejected: { label: 'مرفوض', color: 'bg-red-100 text-red-700' },
};

const ParcelCard = ({ parcel, onCancelled }) => {
  const st = STATUS_CFG[parcel.status] || { label: parcel.status, color: 'bg-gray-100 text-gray-600' };
  const cancellable = ['draft', 'payment_pending', 'payment_submitted', 'paid', 'waiting_delivery', 'waiting_transportation'].includes(parcel.status);

  const cancel = async () => {
    if (!window.confirm('هل تريد إلغاء هذا الطرد؟')) return;
    try {
      await parcelAPI.cancel(parcel.id);
      onCancelled(parcel.id);
    } catch { alert('تعذّر إلغاء الطرد'); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${st.color}`}>{st.label}</span>
        <span className="font-mono text-xs font-bold text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1">#{parcel.trackingNumber}</span>
      </div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm">
          <p className="font-bold text-gray-800">إلى: {parcel.receiverName}</p>
          <p className="text-xs text-gray-500" dir="ltr">{parcel.receiverPhone}</p>
        </div>
        <p className="font-black text-emerald-600 tabular-nums">{arPrice(parcel.totalPrice)} ج.س</p>
      </div>
      {parcel.trip && (
        <p className="text-xs text-gray-500 mb-2">
          🚌 {parcel.trip.fromCity} ← {parcel.trip.toCity} — {new Date(parcel.trip.departureTime).toLocaleDateString('ar-SA', { day: 'numeric', month: 'short' })}
        </p>
      )}
      {parcel.departureOffice && parcel.destinationOffice && (
        <p className="text-xs text-gray-500 mb-3">🏢 {parcel.departureOffice.name} ← {parcel.destinationOffice.name}</p>
      )}
      <div className="flex gap-2 mt-3">
        <a href={`/track-parcel/${parcel.trackingNumber}`} target="_blank" rel="noopener noreferrer"
          className="text-xs font-bold text-blue-600 hover:underline">تتبّع الطرد</a>
        {cancellable && (
          <button onClick={cancel} className="text-xs font-bold text-red-600 hover:underline mr-auto">إلغاء</button>
        )}
      </div>
    </div>
  );
};

const PassengerParcelsPage = () => {
  const navigate = useNavigate();
  const isLoggedIn = !!JSON.parse(localStorage.getItem('passenger_user') || 'null')?.id;
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchParcels = useCallback(async () => {
    if (!isLoggedIn) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await parcelAPI.getMine();
      setParcels(res.data.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [isLoggedIn]);

  useEffect(() => { fetchParcels(); }, [fetchParcels]);
  useEffect(() => { if (!isLoggedIn) navigate('/login?from=/parcels'); }, [isLoggedIn, navigate]);

  if (!isLoggedIn) return null;

  return (
    <div dir="rtl" className="min-h-screen bg-[#F5F9FF] pb-16">
      <AppNavbar />
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-black text-[#1E3A8A]">📦 طرودي</h1>
            <p className="text-sm text-gray-600 mt-0.5">تابع حالة طرودك المرسلة</p>
          </div>
          <button onClick={() => navigate('/parcels/new')} className="bg-[#1E40AF] hover:bg-[#1D4ED8] text-white text-sm font-bold px-5 py-2.5 rounded-xl transition-all">
            + إرسال طرد جديد
          </button>
        </div>

        {!isLoggedIn ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
            <p className="text-gray-600 font-semibold mb-4">سجّل الدخول لعرض طرودك</p>
            <button onClick={() => navigate('/login?from=/parcels')} className="bg-[#1E40AF] text-white font-bold px-6 py-2.5 rounded-xl">تسجيل الدخول</button>
          </div>
        ) : loading ? (
          <div className="text-center py-16 text-gray-400">جاري التحميل...</div>
        ) : parcels.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
            <div className="text-5xl mb-3 opacity-60">📦</div>
            <p className="text-gray-600 font-semibold">لا توجد طرود بعد</p>
          </div>
        ) : (
          <div className="space-y-4">
            {parcels.map((p) => (
              <ParcelCard key={p.id} parcel={p} onCancelled={(id) => setParcels((prev) => prev.filter((x) => x.id !== id))} />
            ))}
          </div>
        )}
      </div>
      <div className="hidden md:block"><SiteFooter navigate={navigate} /></div>
      <MobileBottomNav />
    </div>
  );
};

export default PassengerParcelsPage;
